import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/common';
import { authApi } from '@/lib/api';
import { useAuthStore, User } from '@/stores/authStore';
import { PRESET_AVATARS, getAvatarPreset } from '@/lib/gamification';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
}

type TabType = 'info' | 'avatar' | 'preferences';

export default function EditProfileModal({ isOpen, onClose, user }: EditProfileModalProps) {
  const { t, i18n } = useTranslation();
  const { updateUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [name, setName] = useState(user.name);
  const [nameKo, setNameKo] = useState(user.nameKo || '');
  const [bio, setBio] = useState(user.bio || '');
  const [location, setLocation] = useState(user.location || '');
  const [selectedAvatar, setSelectedAvatar] = useState(user.avatarPreset || 'default');
  const [language, setLanguage] = useState<'en' | 'ko'>(user.language);

  if (!isOpen) return null;

  const handleSave = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await authApi.updateProfile({
        name,
        nameKo: nameKo || undefined,
        bio: bio || undefined,
        location: location || undefined,
        avatarPreset: selectedAvatar,
        language,
      });

      if (response.success && response.user) {
        updateUser(response.user);
        // Change app language if changed
        if (language !== user.language) {
          i18n.changeLanguage(language);
        }
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const tabs = [
    { id: 'info' as TabType, label: 'Basic Info' },
    { id: 'avatar' as TabType, label: 'Avatar' },
    { id: 'preferences' as TabType, label: 'Preferences' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-[calc(100vw-2rem)] sm:max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Edit Profile
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-slate-700">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-coral-600 border-b-2 border-coral-600'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {/* Basic Info Tab */}
          {activeTab === 'info' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Korean Name (optional)
                </label>
                <input
                  type="text"
                  value={nameKo}
                  onChange={(e) => setNameKo(e.target.value)}
                  placeholder="e.g., 김철수"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Bio
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Tell us about yourself..."
                  rows={3}
                  maxLength={200}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent resize-none"
                />
                <p className="mt-1 text-xs text-gray-500">{bio.length}/200 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="e.g., Seoul, South Korea"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          {/* Avatar Tab */}
          {activeTab === 'avatar' && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Choose an avatar that represents you
              </p>
              <div className="grid grid-cols-4 gap-3">
                {PRESET_AVATARS.map((avatar) => (
                  <button
                    key={avatar.id}
                    onClick={() => setSelectedAvatar(avatar.id)}
                    className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                      selectedAvatar === avatar.id
                        ? 'bg-coral-100 dark:bg-coral-900/30 ring-2 ring-coral-500'
                        : 'bg-gray-50 dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600'
                    }`}
                  >
                    <span className="text-3xl mb-1">{avatar.emoji}</span>
                    <span className="text-xs text-gray-600 dark:text-gray-300 text-center">
                      {avatar.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Language
                </label>
                <div className="flex gap-3">
                  <button
                    onClick={() => setLanguage('en')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      language === 'en'
                        ? 'border-coral-500 bg-coral-50 dark:bg-coral-900/20'
                        : 'border-gray-200 dark:border-slate-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">🇺🇸</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">English</div>
                  </button>
                  <button
                    onClick={() => setLanguage('ko')}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                      language === 'ko'
                        ? 'border-coral-500 bg-coral-50 dark:bg-coral-900/20'
                        : 'border-gray-200 dark:border-slate-600 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl mb-1">🇰🇷</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">한국어</div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900/50">
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
          {success && (
            <p className="text-sm text-green-600">Profile updated successfully!</p>
          )}
          {!error && !success && <div />}

          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isLoading || !name.trim()}>
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
